import type { DataGridFilterOption, DataGridFilterOptionsLoader, DataGridFilterOptionsPage } from '@ibsys/kit';
import { queryClient } from '@/shared/api';
import { getShortName } from '@/shared/lib';
import { fetchConversationsClients, fetchConversationsEmployees } from './api';
import { conversationsKeys, toPersonIdsParam } from './hooks';
import type { ClientListItem } from './types';

/**
 * Участник диалога в справочниках фильтров: клиенты и сотрудники описаны одинаково,
 * поэтому в опции они превращаются общим правилом.
 */
type PersonOptionItem = Pick<ClientListItem, 'id' | 'firstName' | 'lastName' | 'middleName'>;

/**
 * Время жизни загруженной страницы справочника участников.
 * Фильтр, открытый повторно, показывает список сразу: за минуту состав справочника не меняется.
 */
const OPTIONS_STALE_TIME = 60_000;

const toPersonOption = (person: PersonOptionItem): DataGridFilterOption => ({
  value: person.id,
  label: getShortName(person.firstName, person.lastName, person.middleName),
});

/**
 * Страница ответа справочника как страница опций фильтра.
 * Следующая страница есть, пока показанные участники не покрыли весь справочник.
 */
const toOptionsPage = (persons: PersonOptionItem[], total: number, offset: number): DataGridFilterOptionsPage => ({
  options: persons.map(toPersonOption),
  hasMore: offset + persons.length < total,
});

/**
 * Асинхронный источник опций фильтра «Клиент» для DataGrid.
 *
 * Справочник клиентов большой, поэтому список грузится страницами с серверным поиском.
 * Ссылка на функцию модульная (стабильная) — кит держит по ней кеш подписей выбранных значений,
 * и на каждый рендер грида кеш не промахивается.
 *
 * Отменой запроса владеет кеш, а не грид: запрос общий для всех, кто ждёт ту же страницу
 * справочника, и оборвать его сигналом одного фильтра нельзя — отмену получили бы и остальные
 * (в StrictMode это сам же фильтр на повторном монтировании, и список открывался с ошибкой).
 * Ответ на устаревший запрос грид отбрасывает сам — по номеру запроса, а не по сигналу.
 */
export const loadConversationClientOptions: DataGridFilterOptionsLoader = async ({ search, page, pageSize }) => {
  const offset = page * pageSize;
  const params = { limit: pageSize, offset, 'client-name': search || undefined };
  const { total, clients } = await queryClient.fetchQuery({
    queryKey: [...conversationsKeys.clients(), { ...params }],
    queryFn: ({ signal }) => fetchConversationsClients(params, signal),
    staleTime: OPTIONS_STALE_TIME,
    retry: false,
  });
  return toOptionsPage(clients, total, offset);
};

/**
 * Подписи выбранных клиентов, которых нет ни в одной загруженной странице:
 * отбор восстановлен из настроек таблицы, и без догрузки имён в описании отбора и в карточке
 * панели фильтров стояли бы сырые идентификаторы.
 */
export const resolveConversationClientOptions = async (
  values: Array<string | number>,
): Promise<DataGridFilterOption[]> => {
  const ids = toPersonIdsParam(values);
  if (!ids) return [];
  const { clients } = await queryClient.fetchQuery({
    queryKey: conversationsKeys.clientsByIds(ids),
    queryFn: ({ signal }) => fetchConversationsClients({ 'client-id': ids }, signal),
    staleTime: OPTIONS_STALE_TIME,
    retry: false,
  });
  return clients.map(toPersonOption);
};

/**
 * Асинхронный источник опций фильтра «Консультант» для DataGrid.
 * Устроен так же, как {@link loadConversationClientOptions}.
 */
export const loadConversationEmployeeOptions: DataGridFilterOptionsLoader = async ({ search, page, pageSize }) => {
  const offset = page * pageSize;
  const params = { limit: pageSize, offset, 'employee-name': search || undefined };
  const { total, employees } = await queryClient.fetchQuery({
    queryKey: [...conversationsKeys.employees(), { ...params }],
    queryFn: ({ signal }) => fetchConversationsEmployees(params, signal),
    staleTime: OPTIONS_STALE_TIME,
    retry: false,
  });
  return toOptionsPage(employees, total, offset);
};

/**
 * Подписи выбранных консультантов по их идентификаторам —
 * см. {@link resolveConversationClientOptions}.
 */
export const resolveConversationEmployeeOptions = async (
  values: Array<string | number>,
): Promise<DataGridFilterOption[]> => {
  const ids = toPersonIdsParam(values);
  if (!ids) return [];
  const { employees } = await queryClient.fetchQuery({
    queryKey: conversationsKeys.employeesByIds(ids),
    queryFn: ({ signal }) => fetchConversationsEmployees({ 'employee-id': ids }, signal),
    staleTime: OPTIONS_STALE_TIME,
    retry: false,
  });
  return employees.map(toPersonOption);
};
